import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Rocket, Phone, Menu, ChevronDown, BadgePercent } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Service Areas", to: "/service-areas" },
  { label: "Lifetime Warranty", to: "/lifetime-warranty" },
  { label: "Guarantees", to: "/guarantees" },
  { label: "Maintenance Plan", to: "/maintenance-plan" },
  { label: "Financing", to: "/financing" },
  { label: "Specials", to: "/specials" },
  { label: "Reviews", to: "/reviews" },
  { label: "About", to: "/about-us" },
  { label: "Blog", to: "/blog" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center group" data-testid="header-logo" aria-label="Aquila Air & Heating home">
      <img src="/logo-header.webp" alt="Aquila Air & Heating — Comfort Ready for Liftoff" className="h-12 lg:h-16 w-auto" width="1206" height="356" decoding="async" fetchPriority="high" />
    </Link>
  );
}

export default function Header() {
  const { company } = useCompany();
  const [openServices, setOpenServices] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const { data: services = [] } = useQuery({
    queryKey: ["services-nav"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const grouped = services.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <>
      <div className="bg-launch text-white text-xs sm:text-sm font-bold" data-testid="announcement-bar">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-0.5 py-2 text-center">
          <Link to="/contact-us" onClick={() => trackEvent("announcement_click", { location: "top_bar" })} className="hover:text-white/80 transition-colors">
            ⚡ AC TROUBLE? DON'T WAIT — GET HELP NOW →
          </Link>
          <span className="hidden sm:inline text-white/40">•</span>
          <Link to="/specials" onClick={() => trackEvent("announcement_click", { location: "top_bar_offer" })} data-testid="announcement-match-beat" className="inline-flex items-center gap-1.5 hover:text-white/80 transition-colors">
            <BadgePercent className="h-4 w-4" /> WE'LL MATCH &amp; BEAT ANY ADVERTISED OFFER
          </Link>
        </div>
      </div>
      <header className="sticky top-0 z-50 bg-[#080b11]/95 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            <div
              className="relative"
              onMouseEnter={() => setOpenServices(true)}
              onMouseLeave={() => setOpenServices(false)}
            >
              <button
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-200 hover:text-white transition-colors"
                data-testid="nav-services-trigger"
                onClick={() => navigate("/services")}
              >
                Services <ChevronDown className="h-4 w-4" />
              </button>
              {openServices && (
                <div className="absolute left-0 top-full w-[640px] bg-white rounded-lg shadow-xl border border-slate-200 p-6 grid grid-cols-2 gap-x-8 gap-y-4" data-testid="nav-services-menu">
                  {Object.entries(grouped).map(([cat, list]) => (
                    <div key={cat}>
                      <div className="text-xs font-bold uppercase tracking-wider text-launch mb-2">{cat}</div>
                      <ul className="space-y-1">
                        {list.map((s) => (
                          <li key={s.slug}>
                            <Link to={`/${s.slug}`} className="text-sm text-slate-600 hover:text-navy transition-colors" data-testid={`nav-service-${s.slug}`}>
                              {s.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="px-3 py-2 text-sm font-semibold text-slate-200 hover:text-white transition-colors" data-testid={`nav-${n.to.slice(1)}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${company.phone_href || ""}`}
              onClick={() => trackEvent("phone_click", { location: "header" })}
              data-testid="header-phone"
              className="hidden sm:flex items-center gap-2 text-white font-bold hover:text-launch transition-colors"
            >
              <Phone className="h-4 w-4" /> {company.phone || ""}
            </a>
            <Link
              to="/contact-us"
              data-testid="header-cta"
              className="hidden md:inline-flex bg-launch hover:bg-launch-dark text-white font-bold py-2.5 px-5 rounded-md text-sm shadow-sm transition-colors"
            >
              Schedule Service
            </Link>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-white" aria-label="Open menu" data-testid="mobile-nav-toggle">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] overflow-y-auto">
                <div className="mt-6 flex flex-col gap-1">
                  <Link to="/services" onClick={() => setMobileOpen(false)} className="py-2 font-semibold text-navy" data-testid="mobile-nav-services">All Services</Link>
                  {services.map((s) => (
                    <Link key={s.slug} to={`/${s.slug}`} onClick={() => setMobileOpen(false)} className="py-1.5 pl-3 text-sm text-slate-600">{s.name}</Link>
                  ))}
                  <div className="h-px bg-slate-200 my-2" />
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="py-2 font-semibold text-navy" data-testid={`mobile-nav-${n.to.slice(1)}`}>{n.label}</Link>
                  ))}
                  <Link to="/careers" onClick={() => setMobileOpen(false)} className="py-2 font-semibold text-navy">Careers</Link>
                  <Link to="/contact-us" onClick={() => setMobileOpen(false)} className="py-2 font-semibold text-navy">Contact</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
    </>
  );
}
